import { useEffect, useRef, useState } from 'react'
import Spinner from './Spinner'
import { ICONS } from '../../common/constants'

const THRESHOLD = 70
const MAX_PULL_DISTANCE = 110
const RESISTANCE = 0.5

// Touches inside modals or already-scrolled inner containers
// should scroll normally, not trigger a refresh
function canStartFrom(target) {
    let el = target
    while (el && el !== document.body) {
        if (el.classList?.contains('modal')) {
            return false
        }
        if (el.scrollTop > 0) {
            return false
        }
        el = el.parentElement
    }
    return true
}

export default function PullToRefresh({ onRefresh }) {
    const [pullDistance, setPullDistance] = useState(0)
    const [refreshing, setRefreshing] = useState(false)

    // Listeners are attached once, so they read current values through refs
    const gestureRef = useRef(null)
    const pullDistanceRef = useRef(0)
    const refreshingRef = useRef(false)
    const onRefreshRef = useRef(onRefresh)
    onRefreshRef.current = onRefresh

    useEffect(function () {
        function updatePullDistance(value) {
            pullDistanceRef.current = value
            setPullDistance(value)
        }

        function handleTouchStart(e) {
            gestureRef.current = null
            if (refreshingRef.current || e.touches.length !== 1) {
                return
            }
            if (window.scrollY > 0 || !canStartFrom(e.target)) {
                return
            }
            gestureRef.current = {
                startX: e.touches[0].clientX,
                startY: e.touches[0].clientY,
                pulling: false,
            }
        }

        function handleTouchMove(e) {
            const gesture = gestureRef.current
            if (!gesture) {
                return
            }
            const dx = e.touches[0].clientX - gesture.startX
            const dy = e.touches[0].clientY - gesture.startY

            if (!gesture.pulling) {
                // Horizontal swipe or regular upward scroll
                if (dy <= 0 || Math.abs(dx) > Math.abs(dy)) {
                    gestureRef.current = null
                    return
                }
                gesture.pulling = true
            }

            // Blocks the browser's native pull-to-refresh / overscroll bounce
            if (e.cancelable) {
                e.preventDefault()
            }
            updatePullDistance(
                Math.min(MAX_PULL_DISTANCE, Math.max(0, dy * RESISTANCE)),
            )
        }

        async function handleTouchEnd() {
            const wasPulling = gestureRef.current?.pulling
            gestureRef.current = null
            if (!wasPulling) {
                return
            }

            if (pullDistanceRef.current < THRESHOLD) {
                updatePullDistance(0)
                return
            }

            refreshingRef.current = true
            setRefreshing(true)
            updatePullDistance(THRESHOLD)
            try {
                await onRefreshRef.current()
            } finally {
                refreshingRef.current = false
                setRefreshing(false)
                updatePullDistance(0)
            }
        }

        document.documentElement.classList.add('pull-to-refresh-enabled')
        window.addEventListener('touchstart', handleTouchStart, {
            passive: true,
        })
        window.addEventListener('touchmove', handleTouchMove, {
            passive: false,
        })
        window.addEventListener('touchend', handleTouchEnd)
        window.addEventListener('touchcancel', handleTouchEnd)

        return function () {
            document.documentElement.classList.remove('pull-to-refresh-enabled')
            window.removeEventListener('touchstart', handleTouchStart)
            window.removeEventListener('touchmove', handleTouchMove)
            window.removeEventListener('touchend', handleTouchEnd)
            window.removeEventListener('touchcancel', handleTouchEnd)
        }
    }, [])

    if (!pullDistance && !refreshing) {
        return null
    }

    const progress = Math.min(1, pullDistance / THRESHOLD)

    return (
        <div
            className="pull-to-refresh-indicator"
            style={{
                transform: `translate(-50%, ${pullDistance}px)`,
                opacity: progress,
            }}
        >
            {refreshing ? (
                <Spinner size="tiny" />
            ) : (
                <img
                    src={ICONS.ARROW_DOWN_WHITE}
                    alt="Pull to refresh"
                    width="20"
                    height="20"
                    style={{ transform: `rotate(${progress * 180}deg)` }}
                />
            )}
        </div>
    )
}
